import { useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError, type CurrentUser } from './api';
import { SignIn } from './pages/SignIn';
import { PendingAccess } from './pages/PendingAccess';
import { Dashboard } from './pages/Dashboard';
import { AdminSettings } from './pages/AdminSettings';
import { AuditLog } from './pages/AuditLog';
import { AdminFeedback } from './pages/AdminFeedback';
import { SystemSettings } from './pages/SystemSettings';
import { Archive } from './pages/Archive';
import { AdminUsers } from './pages/AdminUsers';
import { MyRequests } from './pages/MyRequests';
import { ApprovedRequests } from './pages/ApprovedRequests';
import { RequestDetailPage } from './pages/RequestDetail';
import { Wizard } from './wizard/Wizard';
import { ApprovalQueue } from './pages/ApprovalQueue';
import { ItQueue } from './pages/ItQueue';
import { AppShell } from './components/AppShell';
import { Walkthrough } from './components/Walkthrough';
import { Alert, Spinner } from './components/ui';
import { canWorkItQueue, isAdmin, isApprovedViewer, isApprover, isSystemAdmin } from './roles';

export function useCurrentUser() {
  return useQuery<CurrentUser | null>({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        const { user } = await api.me();
        return user;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
  });
}

export function App() {
  // Local, so finishing the tour closes it immediately rather than waiting on
  // a refetch. The server write is what stops it coming back tomorrow.
  const [dismissed, setDismissed] = useState(false);
  const location = useLocation();
  const { data: user, isPending, isError, error } = useCurrentUser();

  if (isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="max-w-md">
          <Alert>
            <p className="font-medium">Could not reach the server.</p>
            <p className="mt-1">{error instanceof Error ? error.message : 'Unknown error'}</p>
          </Alert>
        </div>
      </div>
    );
  }

  if (!user) {
    // Everything is behind sign-in; /signin renders the Microsoft button.
    return (
      <Routes>
        <Route path="/signin" element={<SignIn />} />
        <Route path="*" element={<Navigate to="/signin" replace state={{ from: location.pathname }} />} />
      </Routes>
    );
  }

  // Shown to anybody who has never seen it, including while they are still
  // pending: that state is the most confusing one to land in cold.
  const showWalkthrough = user.walkthroughSeenAt === null && !dismissed;

  // Signed in but no role yet: the pending-access state from the diagram.
  if (user.role === 'PENDING') {
    return (
      <>
        {showWalkthrough && <Walkthrough user={user} onDone={() => setDismissed(true)} />}
        <Routes>
          <Route path="*" element={<PendingAccess user={user} />} />
        </Routes>
      </>
    );
  }

  // The approved-requests viewer: one list and one read-only detail screen.
  // Its own route table rather than conditions inside the shared one, so a
  // screen added later stays closed to the viewer until somebody opens it here.
  if (isApprovedViewer(user.role)) {
    return (
      <AppShell user={user}>
        {showWalkthrough && <Walkthrough user={user} onDone={() => setDismissed(true)} />}
        <Routes>
          <Route path="/" element={<ApprovedRequests />} />
          <Route path="/requests/:id" element={<RequestDetailPage user={user} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppShell>
    );
  }

  return (
    <AppShell user={user}>
      {showWalkthrough && <Walkthrough user={user} onDone={() => setDismissed(true)} />}
      <Routes>
        <Route path="/" element={<Dashboard user={user} />} />
        <Route path="/signin" element={<Navigate to="/" replace />} />
        <Route path="/requests" element={<MyRequests user={user} />} />
        <Route path="/archive" element={<Archive />} />
        <Route path="/requests/:id" element={<RequestDetailPage user={user} />} />
        <Route path="/requests/:id/edit" element={<Wizard user={user} />} />
        {(isApprover(user.role)) && (
          <Route path="/queue" element={<ApprovalQueue />} />
        )}
        {canWorkItQueue(user.role) && <Route path="/it-queue" element={<ItQueue />} />}
        {isAdmin(user.role) && <Route path="/admin/users" element={<AdminUsers />} />}
        {isAdmin(user.role) && <Route path="/admin/settings" element={<AdminSettings />} />}
        {isSystemAdmin(user.role) && <Route path="/admin/system" element={<SystemSettings />} />}
        {isSystemAdmin(user.role) && <Route path="/admin/audit" element={<AuditLog />} />}
        {isSystemAdmin(user.role) && <Route path="/admin/feedback" element={<AdminFeedback />} />}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
}
